import { useState, useEffect, useCallback } from 'react';
import { Achievement, AchievementStats } from '../types';
import { achievementsAPI } from '../services/achievementsAPI';

export const useAchievements = () => {
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [stats, setStats] = useState<AchievementStats | null>(null);
  const [newAchievements, setNewAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cargar progreso de achievements
  const loadProgress = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await achievementsAPI.getProgress();
      setAchievements(response.data.data.achievements);
      
      if (response.data.data.stats) {
        setStats(response.data.data.stats);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error cargando achievements');
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar nuevos achievements
  const loadNewAchievements = useCallback(async () => {
    try {
      const response = await achievementsAPI.getNew();
      setNewAchievements(response.data.data.achievements);
    } catch (err: any) {
      console.warn('Error loading new achievements:', err);
    }
  }, []);

  // Cargar estadísticas
  const loadStats = useCallback(async () => {
    try {
      const response = await achievementsAPI.getStats();
      setStats(response.data.data.stats);
    } catch (err: any) {
      console.warn('Error loading achievement stats:', err);
    }
  }, []);

  // Marcar achievement como visto
  const markAsSeen = useCallback(async (achievementId: number) => {
    try {
      await achievementsAPI.markAsSeen(achievementId);
      
      // Actualizar la lista local
      setAchievements(prev =>
        prev.map(achievement =>
          achievement.id === achievementId
            ? { ...achievement, is_new: false }
            : achievement
        )
      );
      
      // Remover de nuevos achievements
      setNewAchievements(prev =>
        prev.filter(achievement => achievement.id !== achievementId)
      );
      
      // Actualizar estadísticas
      if (stats) {
        setStats(prev => prev ? { ...prev, new_achievements: prev.new_achievements - 1 } : null);
      }
    } catch (err: any) {
      console.error('Error marking achievement as seen:', err);
    }
  }, [stats]);

  // Marcar todos como vistos
  const markAllAsSeen = useCallback(async () => {
    try {
      await achievementsAPI.markAllAsSeen();
      
      // Actualizar listas locales
      setAchievements(prev =>
        prev.map(achievement => ({ ...achievement, is_new: false }))
      );
      setNewAchievements([]);
      
      // Actualizar estadísticas
      if (stats) {
        setStats(prev => prev ? { ...prev, new_achievements: 0 } : null);
      }
    } catch (err: any) {
      console.error('Error marking all achievements as seen:', err);
    }
  }, [stats]);

  // Procesar nuevos achievements de una respuesta de API
  const processNewAchievements = useCallback((newAchievementsList: Achievement[]) => {
    if (newAchievementsList && newAchievementsList.length > 0) {
      setNewAchievements(prev => {
        // Evitar duplicados
        const existingIds = prev.map(a => a.id);
        const filtered = newAchievementsList.filter(a => !existingIds.includes(a.id));
        return [...prev, ...filtered];
      });
      
      // Recargar progreso y estadísticas
      loadProgress();
    }
  }, [loadProgress]);

  // Efecto inicial
  useEffect(() => {
    loadProgress();
    loadNewAchievements();
  }, [loadProgress, loadNewAchievements]);

  return {
    achievements,
    stats,
    newAchievements,
    loading,
    error,
    loadProgress,
    loadNewAchievements,
    loadStats,
    markAsSeen,
    markAllAsSeen,
    processNewAchievements
  };
};